/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { format, addDays, startOfDay, isBefore } from 'date-fns';
import type { Station, SnapRecentSearchEntry, RoomConfig } from '../types';
import { TripType } from '../types';
import {
  DEFAULT_DEPARTURE_OFFSET_DAYS,
  DEFAULT_STAY_DAYS,
  DEFAULT_RECENT_SEARCH_ADULTS,
} from '../configs/constants';

export type AnalyticsPayload = Record<string, unknown>;

// ── Recent Searches Utility Functions ───────────────────────────────────────

/**
 * Generates a unique route key from origin and destination stations.
 * Includes tripType so that RT and OW searches for the same route
 * are treated as separate entries (fix: HOTEL-28586).
 */
export const getRouteKey = (from: Station, to: Station, tripType?: TripType) =>
  tripType
    ? `${from.StationCode}-${to.StationCode}-${tripType}`
    : `${from.StationCode}-${to.StationCode}`;

/**
 * Checks if a given date string is expired (before today).
 * Used for validating stored recent search dates (Scenario 5).
 */
export const isDateExpired = (dateStr: string): boolean => {
  const today = startOfDay(new Date());
  const date = new Date(dateStr);
  return isBefore(date, today);
};

/**
 * Returns fallback defaults when the stored dates have expired.
 * 7 days from today + 2 nights as checkout, 2 adults & 1 room.
 */
export const getExpiredDateFallback = (): {
  departDate: string;
  returnDate: string;
  rooms: RoomConfig[];
} => {
  const today = startOfDay(new Date());
  const depart = addDays(today, DEFAULT_DEPARTURE_OFFSET_DAYS);
  const returnDate = addDays(depart, DEFAULT_STAY_DAYS);
  return {
    departDate: format(depart, 'yyyy-MM-dd'),
    returnDate: format(returnDate, 'yyyy-MM-dd'),
    rooms: [
      {
        id: '1',
        adults: DEFAULT_RECENT_SEARCH_ADULTS,
        children: [],
        infants: 0,
      },
    ],
  };
};

/**
 * Returns a resolved SnapRecentSearchEntry, applying expired-date fallback
 * if the depart date has passed (Scenario 5).
 */
export const resolveRecentSearch = (
  entry: SnapRecentSearchEntry,
): SnapRecentSearchEntry => {
  if (isDateExpired(entry.departDate)) {
    const fallback = getExpiredDateFallback();
    return {
      ...entry,
      departDate: fallback.departDate,
      returnDate:
        entry.tripType === TripType.ROUND_TRIP
          ? fallback.returnDate
          : undefined,
      hotelCheckIn: undefined,
      hotelCheckOut: undefined,
      selectDifferentDates: false,
      rooms: fallback.rooms,
    };
  }
  return entry;
};

// ── Analytics Utility Functions ─────────────────────────────────────────────

const resolveAnalyticsParams = <
  T extends { nativeParams: AnalyticsPayload; webParams: AnalyticsPayload },
>(
  analyticsBuilderResult: T,
  platform: string,
): T['nativeParams'] | T['webParams'] => {
  switch (platform) {
    case 'android':
    case 'ios':
      return analyticsBuilderResult.nativeParams;

    case 'web':
      return analyticsBuilderResult.webParams;

    default:
      return analyticsBuilderResult.nativeParams;
  }
};

export { resolveAnalyticsParams };
